import React, { useState } from 'react';
import {
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  FormControl,
  FormLabel,
  Select,
  HStack,
  VStack,
  Button,
  Tooltip,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  SliderMark,
  RadioGroup,
  Radio,
  Text,
  useColorMode,
  useColorModeValue,
  Icon,
  SimpleGrid,
  Badge,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Portal,
  Flex,
  Divider,
  ButtonGroup,
  Grid,
  GridItem,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Input,
} from '@chakra-ui/react';
import { HexColorPicker } from 'react-colorful';
import { FiType, FiLayout, FiBox, FiDroplet, FiMaximize, FiMinimize, FiAlignLeft, FiAlignCenter, FiAlignRight, FiClock, FiBold, FiGrid } from 'react-icons/fi';

const defaultStyles = {
  message: {
    color: '#1A202C',
    fontSize: 'medium',
    fontWeight: 'normal',
    textAlign: 'center',
  },
  container: {
    backgroundColor: '#FFF9E6',
    borderStyle: 'solid',
    borderColor: '#FFB74D',
    borderWidth: '2px',
    borderRadius: '8px',
    padding: '16px',
  },
  countdown: {
    color: '#FF5722',
    size: 'large',
    spacing: 'medium',
  },
  theme: 'default'
};

const presetThemes = {
  default: {
    name: 'Classic',
    description: 'Clean and professional design',
    message: { color: '#1A202C', fontSize: 'medium', fontWeight: 'normal', textAlign: 'center' },
    container: { backgroundColor: '#F7FAFC', borderStyle: 'solid', borderColor: '#4A5568', borderWidth: '2px', borderRadius: '8px', padding: '16px' },
    countdown: { color: '#2D3748', size: 'large', spacing: 'medium' }
  },
  urgent: {
    name: 'Urgent',
    description: 'High-impact, attention-grabbing style',
    message: { color: '#E53E3E', fontSize: 'large', fontWeight: 'bold', textAlign: 'center' },
    container: { backgroundColor: '#FFF5F5', borderStyle: 'solid', borderColor: '#F56565', borderWidth: '3px', borderRadius: '12px', padding: '24px' },
    countdown: { color: '#C53030', size: 'large', spacing: 'large' }
  },
  professional: {
    name: 'Business',
    description: 'Subtle and sophisticated appearance',
    message: { color: '#2B6CB0', fontSize: 'medium', fontWeight: 'medium', textAlign: 'center' },
    container: { backgroundColor: '#EBF8FF', borderStyle: 'solid', borderColor: '#4299E1', borderWidth: '1px', borderRadius: '4px', padding: '16px' },
    countdown: { color: '#2C5282', size: 'medium', spacing: 'medium' }
  },
  celebration: {
    name: 'Premium',
    description: 'Luxurious and eye-catching design',
    message: { color: '#2F855A', fontSize: 'large', fontWeight: 'bold', textAlign: 'center' },
    container: { backgroundColor: '#F0FFF4', borderStyle: 'dashed', borderColor: '#48BB78', borderWidth: '2px', borderRadius: '16px', padding: '24px' },
    countdown: { color: '#276749', size: 'large', spacing: 'large' }
  }
};

const ColorPickerButton = ({ color, onChange, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const handleColorChange = (newColor) => {
    onChange(newColor);
  };

  return (
    <Popover
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      placement="right"
      closeOnBlur={true}
      closeOnEsc={true}
    >
      <PopoverTrigger>
        <Button
          width="full"
          height="40px"
          p={2}
          variant="outline"
          borderWidth={2}
          bg={color}
          onClick={() => setIsOpen(!isOpen)}
          _hover={{ transform: 'translateY(-1px)', shadow: 'md' }}
          transition="all 0.2s"
        >
          <HStack width="full" justify="space-between">
            <Text color={useColorModeValue('gray.800', 'white')}>{label}</Text>
            <Box
              width="24px"
              height="24px"
              borderRadius="md"
              border="2px"
              borderColor={borderColor}
              bg={color}
            />
          </HStack>
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent width="auto" border="none" shadow="lg" p={3}>
          <PopoverBody p={0}>
            <VStack spacing={3}>
              <Box>
                <HexColorPicker color={color} onChange={handleColorChange} />
              </Box>
              <Input
                value={color}
                onChange={(e) => handleColorChange(e.target.value)}
                size="sm"
                textAlign="center"
                fontFamily="mono"
                spellCheck="false"
                variant="filled"
                placeholder="#000000"
              />
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

const MessageStyles = ({ styles = defaultStyles, onChange }) => {
  const [activeColor, setActiveColor] = useState(null);
  const [openSections, setOpenSections] = useState({
    textStyle: false,
    containerStyle: false,
    timerStyle: false
  });
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const currentStyles = { ...defaultStyles, ...styles };

  const handleStyleChange = (category, property, value) => {
    onChange({
      ...currentStyles,
      [category]: {
        ...currentStyles[category],
        [property]: value
      }
    });
  };

  const handleSectionToggle = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleThemeChange = (themeName) => {
    onChange({
      ...presetThemes[themeName],
      theme: themeName
    });
  };

  const StyleSection = ({ title, icon, children, section }) => (
    <Box
      bg={useColorModeValue('gray.50', 'gray.700')}
      borderRadius="lg"
      border="1px"
      borderColor={borderColor}
      overflow="hidden"
    >
      <Accordion allowToggle index={openSections[section] ? 0 : -1} onChange={() => handleSectionToggle(section)}>
        <AccordionItem border="none">
          <h2>
            <AccordionButton py={4} px={4}>
              <HStack flex="1">
                <Icon as={icon} color="purple.500" />
                <Text fontWeight="bold">{title}</Text>
              </HStack>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4} px={4}>
            {children}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Box>
  );

  return (
    <VStack spacing={6} align="stretch">
      <Accordion allowToggle>
        {/* Text Style - Colors & Size */}
        <AccordionItem>
          {({ isExpanded }) => (
            <>
              <h2>
                <AccordionButton
                  py={2}
                  px={3}
                  bg={isExpanded ? 'purple.50' : 'transparent'}
                  _hover={{ bg: isExpanded ? 'purple.50' : 'gray.50' }}
                >
                  <HStack flex="1">
                    <Icon as={FiType} color="purple.500" />
                    <Text fontWeight="bold">Text Colors & Size</Text>
                  </HStack>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={3} px={3}>
                <VStack spacing={4}>
                  <Box width="full">
                    <ColorPickerButton
                      color={currentStyles.message.color}
                      onChange={(color) => handleStyleChange('message', 'color', color)}
                      label="Text Color"
                    />
                  </Box>
                  <ButtonGroup width="full" size="sm" isAttached variant="outline">
                    <Button
                      flex={1}
                      leftIcon={<Icon as={FiType} fontSize="sm" />}
                      onClick={() => handleStyleChange('message', 'fontSize', 'small')}
                      colorScheme={currentStyles.message.fontSize === 'small' ? 'purple' : 'gray'}
                    >
                      Small
                    </Button>
                    <Button
                      flex={1}
                      leftIcon={<Icon as={FiType} />}
                      onClick={() => handleStyleChange('message', 'fontSize', 'medium')}
                      colorScheme={currentStyles.message.fontSize === 'medium' ? 'purple' : 'gray'}
                    >
                      Medium
                    </Button>
                    <Button
                      flex={1}
                      leftIcon={<Icon as={FiType} fontSize="lg" />}
                      onClick={() => handleStyleChange('message', 'fontSize', 'large')}
                      colorScheme={currentStyles.message.fontSize === 'large' ? 'purple' : 'gray'}
                    >
                      Large
                    </Button>
                  </ButtonGroup>
                </VStack>
              </AccordionPanel>
            </>
          )}
        </AccordionItem>

        {/* Text Style - Alignment & Weight */}
        <AccordionItem>
          {({ isExpanded }) => (
            <>
              <h2>
                <AccordionButton
                  py={2}
                  px={3}
                  bg={isExpanded ? 'purple.50' : 'transparent'}
                  _hover={{ bg: isExpanded ? 'purple.50' : 'gray.50' }}
                >
                  <HStack flex="1">
                    <Icon as={FiType} color="purple.500" />
                    <Text fontWeight="bold">Text Alignment & Weight</Text>
                  </HStack>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={3} px={3}>
                <VStack spacing={4}>
                  <ButtonGroup width="full" size="sm" isAttached variant="outline">
                    <Button
                      flex={1}
                      leftIcon={<FiAlignLeft />}
                      onClick={() => handleStyleChange('message', 'textAlign', 'left')}
                      colorScheme={currentStyles.message.textAlign === 'left' ? 'purple' : 'gray'}
                    >
                      Left
                    </Button>
                    <Button
                      flex={1}
                      leftIcon={<FiAlignCenter />}
                      onClick={() => handleStyleChange('message', 'textAlign', 'center')}
                      colorScheme={currentStyles.message.textAlign === 'center' ? 'purple' : 'gray'}
                    >
                      Center
                    </Button>
                    <Button
                      flex={1}
                      leftIcon={<FiAlignRight />}
                      onClick={() => handleStyleChange('message', 'textAlign', 'right')}
                      colorScheme={currentStyles.message.textAlign === 'right' ? 'purple' : 'gray'}
                    >
                      Right
                    </Button>
                  </ButtonGroup>

                  <ButtonGroup width="full" size="sm" isAttached variant="outline">
                    <Button
                      flex={1}
                      leftIcon={<FiType />}
                      onClick={() => handleStyleChange('message', 'fontWeight', 'normal')}
                      colorScheme={currentStyles.message.fontWeight === 'normal' ? 'purple' : 'gray'}
                    >
                      Regular
                    </Button>
                    <Button
                      flex={1}
                      leftIcon={<FiBold />}
                      onClick={() => handleStyleChange('message', 'fontWeight', 'medium')}
                      colorScheme={currentStyles.message.fontWeight === 'medium' ? 'purple' : 'gray'}
                    >
                      Medium
                    </Button>
                    <Button
                      flex={1}
                      leftIcon={<FiBold />}
                      onClick={() => handleStyleChange('message', 'fontWeight', 'bold')}
                      colorScheme={currentStyles.message.fontWeight === 'bold' ? 'purple' : 'gray'}
                    >
                      Bold
                    </Button>
                  </ButtonGroup>
                </VStack>
              </AccordionPanel>
            </>
          )}
        </AccordionItem>

        {/* Container Style - Colors */}
        <AccordionItem>
          {({ isExpanded }) => (
            <>
              <h2>
                <AccordionButton
                  py={2}
                  px={3}
                  bg={isExpanded ? 'purple.50' : 'transparent'}
                  _hover={{ bg: isExpanded ? 'purple.50' : 'gray.50' }}
                >
                  <HStack flex="1">
                    <Icon as={FiBox} color="purple.500" />
                    <Text fontWeight="bold">Container Colors</Text>
                  </HStack>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={3} px={3}>
                <SimpleGrid columns={2} spacing={4}>
                  <Box>
                    <ColorPickerButton
                      color={currentStyles.container.backgroundColor}
                      onChange={(color) => handleStyleChange('container', 'backgroundColor', color)}
                      label="Background"
                    />
                  </Box>
                  <Box>
                    <ColorPickerButton
                      color={currentStyles.container.borderColor}
                      onChange={(color) => handleStyleChange('container', 'borderColor', color)}
                      label="Border Color"
                    />
                  </Box>
                </SimpleGrid>
              </AccordionPanel>
            </>
          )}
        </AccordionItem>

        {/* Container Style - Border & Spacing */}
        <AccordionItem>
          {({ isExpanded }) => (
            <>
              <h2>
                <AccordionButton
                  py={2}
                  px={3}
                  bg={isExpanded ? 'purple.50' : 'transparent'}
                  _hover={{ bg: isExpanded ? 'purple.50' : 'gray.50' }}
                >
                  <HStack flex="1">
                    <Icon as={FiBox} color="purple.500" />
                    <Text fontWeight="bold">Container Border & Spacing</Text>
                  </HStack>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={3} px={3}>
                <VStack spacing={6}>
                  {/* Border Style */}
                  <Box width="full">
                    <Text mb={3} fontSize="sm" fontWeight="medium" color="gray.600">Border Style</Text>
                    <ButtonGroup width="full" size="md" isAttached variant="outline">
                      <Button
                        flex={1}
                        onClick={() => handleStyleChange('container', 'borderStyle', 'solid')}
                        colorScheme={currentStyles.container.borderStyle === 'solid' ? 'purple' : 'gray'}
                        borderWidth="2px"
                        borderStyle="solid"
                      >
                        Solid
                      </Button>
                      <Button
                        flex={1}
                        onClick={() => handleStyleChange('container', 'borderStyle', 'dashed')}
                        colorScheme={currentStyles.container.borderStyle === 'dashed' ? 'purple' : 'gray'}
                        borderWidth="2px"
                        borderStyle="dashed"
                      >
                        Dashed
                      </Button>
                      <Button
                        flex={1}
                        onClick={() => handleStyleChange('container', 'borderStyle', 'dotted')}
                        colorScheme={currentStyles.container.borderStyle === 'dotted' ? 'purple' : 'gray'}
                        borderWidth="2px"
                        borderStyle="dotted"
                      >
                        Dotted
                      </Button>
                      <Button
                        flex={1}
                        onClick={() => handleStyleChange('container', 'borderStyle', 'double')}
                        colorScheme={currentStyles.container.borderStyle === 'double' ? 'purple' : 'gray'}
                        borderWidth="2px"
                        borderStyle="double"
                      >
                        Double
                      </Button>
                    </ButtonGroup>
                  </Box>

                  {/* Border Width */}
                  <Box width="full">
                    <HStack justify="space-between" mb={2}>
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">Border Width</Text>
                      <Badge colorScheme="purple" fontSize="sm">
                        {currentStyles.container.borderWidth}
                      </Badge>
                    </HStack>
                    <Slider
                      value={parseInt(currentStyles.container.borderWidth)}
                      onChange={(value) => handleStyleChange('container', 'borderWidth', `${value}px`)}
                      min={0}
                      max={5}
                      step={1}
                      colorScheme="purple"
                    >
                      <SliderTrack>
                        <SliderFilledTrack />
                      </SliderTrack>
                      <SliderThumb boxSize={6} boxShadow="lg">
                        <Icon as={FiMaximize} color="purple.500" fontSize="10px" />
                      </SliderThumb>
                    </Slider>
                  </Box>

                  {/* Border Radius */}
                  <Box width="full">
                    <HStack justify="space-between" mb={2}>
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">Border Radius</Text>
                      <Badge colorScheme="purple" fontSize="sm">
                        {currentStyles.container.borderRadius}
                      </Badge>
                    </HStack>
                    <Slider
                      value={parseInt(currentStyles.container.borderRadius)}
                      onChange={(value) => handleStyleChange('container', 'borderRadius', value)}
                      min={0}
                      max={24}
                      step={2}
                      colorScheme="purple"
                    >
                      <SliderTrack>
                        <SliderFilledTrack />
                      </SliderTrack>
                      <SliderThumb boxSize={6} boxShadow="lg">
                        <Icon as={FiMinimize} color="purple.500" fontSize="10px" />
                      </SliderThumb>
                    </Slider>
                  </Box>

                  {/* Container Padding */}
                  <Box width="full">
                    <Text mb={3} fontSize="sm" fontWeight="medium" color="gray.600">Container Padding</Text>
                    <ButtonGroup width="full" size="md" isAttached variant="outline">
                      <Button
                        flex={1}
                        onClick={() => handleStyleChange('container', 'padding', '8px')}
                        colorScheme={currentStyles.container.padding === '8px' ? 'purple' : 'gray'}
                        leftIcon={<Icon as={FiMinimize} />}
                      >
                        Compact
                      </Button>
                      <Button
                        flex={1}
                        onClick={() => handleStyleChange('container', 'padding', '16px')}
                        colorScheme={currentStyles.container.padding === '16px' ? 'purple' : 'gray'}
                        leftIcon={<Icon as={FiBox} />}
                      >
                        Normal
                      </Button>
                      <Button
                        flex={1}
                        onClick={() => handleStyleChange('container', 'padding', '24px')}
                        colorScheme={currentStyles.container.padding === '24px' ? 'purple' : 'gray'}
                        leftIcon={<Icon as={FiMaximize} />}
                      >
                        Spacious
                      </Button>
                    </ButtonGroup>
                  </Box>
                </VStack>
              </AccordionPanel>
            </>
          )}
        </AccordionItem>

        {/* Theme Presets */}
        <AccordionItem>
          {({ isExpanded }) => (
            <>
              <h2>
                <AccordionButton
                  py={2}
                  px={3}
                  bg={isExpanded ? 'purple.50' : 'transparent'}
                  _hover={{ bg: isExpanded ? 'purple.50' : 'gray.50' }}
                >
                  <HStack flex="1">
                    <Icon as={FiGrid} color="purple.500" />
                    <Text fontWeight="bold">Theme Presets</Text>
                  </HStack>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={3} px={3}>
                <SimpleGrid columns={2} spacing={3}>
                  {Object.entries(presetThemes).map(([key, theme]) => (
                    <Box
                      key={key}
                      onClick={() => handleThemeChange(key)}
                      cursor="pointer"
                      position="relative"
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor={currentStyles.theme === key ? 'purple.500' : borderColor}
                      bg={useColorModeValue('white', 'gray.800')}
                      _hover={{
                        borderColor: 'purple.400',
                        shadow: 'md',
                      }}
                      transition="all 0.2s"
                      role="group"
                    >
                      <Box
                        p={3}
                        bg={theme.container.backgroundColor}
                        borderTopRadius="md"
                        borderBottom="2px"
                        borderBottomStyle={theme.container.borderStyle}
                        borderBottomColor={theme.container.borderColor}
                      >
                        <HStack justify="space-between" mb={1}>
                          <Text
                            fontWeight="bold"
                            fontSize="md"
                            color={theme.message.color}
                          >
                            {theme.name}
                          </Text>
                          {currentStyles.theme === key && (
                            <Badge colorScheme="purple" variant="subtle" fontSize="xs">
                              Active
                            </Badge>
                          )}
                        </HStack>
                        <Text
                          fontSize="xs"
                          color={theme.message.color}
                          opacity={0.8}
                          noOfLines={1}
                        >
                          {theme.description}
                        </Text>
                      </Box>
                      <Box
                        position="absolute"
                        bottom={0}
                        left={0}
                        right={0}
                        height="2px"
                        bg={currentStyles.theme === key ? 'purple.500' : 'transparent'}
                        transition="all 0.2s"
                        _groupHover={{ bg: 'purple.400' }}
                      />
                    </Box>
                  ))}
                </SimpleGrid>
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      </Accordion>
    </VStack>
  );
};

export default MessageStyles;